import { type MergeField } from 'entities/MergeField/model/MergeField'
import { IntegrationKey } from 'entities/Integrations'
import type { IWidgetId } from 'entities/Chatbot'
import { type IUser } from 'entities/Users'
import { handoffToHumanTemplate } from 'entities/TemplateActions/actions.tsx'

export type IWidgetQAPair = { q: string; a: string }
type ITagItem = { name: string }

const replaceMentionTags = (text: string, usersMap: Map<number, IUser>): string =>
  text
    .replace(/\{tag:user:(\d+)\}/g, (match, id) => {
      const user = usersMap.get(Number(id))
      return user?.full_name ? `${user.full_name}` : match
    })
    .replace(/\{tag:inbox\}/g, 'inbox')

export const INTEGRATION_DISPLAY_NAME: Partial<Record<IntegrationKey, string>> = {
  [IntegrationKey.hubspot]: 'HubSpot',
  [IntegrationKey.calendly]: 'Calendly',
}

export const formatWidgetMessage = (pairs: IWidgetQAPair[]): string =>
  pairs.map(({ q, a }) => `Q: ${q}\nA: ${a}`).join('\n\n')

const parseAutoMessage = (
  raw: string,
  autoLabel: string,
  answer: string,
  usersMap: Map<number, IUser>
): string => {
  try {
    const data = JSON.parse(raw) as { auto?: boolean; content?: string }
    if (data.auto) return autoLabel
    return data.content ? replaceMentionTags(data.content, usersMap) : answer
  } catch {
    return answer
  }
}

export const parseActionAnswer = (answer: string, usersMap: Map<number, IUser>): string => {
  if (answer === '@stopConversation()') return 'Stop agent'
  if (answer.match(handoffToHumanTemplate.regex)) return handoffToHumanTemplate.label

  const tagMatch = answer.match(/^@(?:addTag|removeTag)\((.+)\)$/)
  if (tagMatch) {
    try {
      const { tags } = JSON.parse(tagMatch[1]) as { tags: ITagItem[] }
      return tags.map((tag) => tag.name).join(', ')
    } catch {
      return answer
    }
  }

  const addNoteMatch = answer.match(/^@(?:addContactNote|addConversationNote)\((.+)\)$/)
  if (addNoteMatch) {
    return parseAutoMessage(
      addNoteMatch[1],
      'AI will write this note based on the conversation',
      answer,
      usersMap
    )
  }

  const sendSMSMatch = answer.match(/^@sendSMS\((.+)\)$/)
  if (sendSMSMatch) {
    return parseAutoMessage(
      sendSMSMatch[1],
      'AI will write this message based on the conversation',
      answer,
      usersMap
    )
  }

  return replaceMentionTags(answer, usersMap)
}

const ACTION_INLINE_LABELS: Record<string, string> = {
  stopConversation: 'stop conversation',
  addTag: 'add tag',
  removeTag: 'remove tag',
  addContactNote: 'add contact note',
  addConversationNote: 'add conversation note',
  sendSMS: 'send SMS',
}

const WIDGET_ID_LABELS: Record<
  Exclude<IWidgetId, 'booking' | 'followup' | 'suggestions'>,
  string
> = {
  knowledge_base: 'knowledge base',
  trigger_conditions: 'trigger conditions',
  fields_selector: 'merge fields selector',
  add_actions: 'actions',
}

const SETTINGS_FLAG_LABELS: Record<string, string> = {
  use_knowledge_base: 'Knowledge base',
}

const buildKeysPattern = (map: Record<string, string>) =>
  new RegExp(`\\b(${Object.keys(map).join('|')})\\b`, 'g')

const widgetIdPattern = new RegExp(
  `"?(\\b(?:${Object.keys(WIDGET_ID_LABELS).join('|')})\\b)"?`,
  'g'
)
const settingsFlagPattern = buildKeysPattern(SETTINGS_FLAG_LABELS)

const parseFallbackTemplate = (template: string): string => {
  const inner = template.replace(/[{}]/g, '').trim()
  const dotIndex = inner.indexOf('.')
  if (dotIndex === -1) {
    return inner.charAt(0).toUpperCase() + inner.slice(1)
  }
  const integrationKey = inner.slice(0, dotIndex)
  const fieldName = inner.slice(dotIndex + 1)
  const name = fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
  return `${name} (${integrationKey})`
}

export const replaceInlineActions = (
  text: string,
  allMergeFields: MergeField[],
  usersMap: Map<number, IUser>
): string => {
  const textWithActions = text.replace(/(?<!\w)@(\w+)(?:\([^)]*\))?/g, (_, name: string) => {
    const label = ACTION_INLINE_LABELS[name] ?? name
    return `"${label}" action`
  })

  const textWithMentions = replaceMentionTags(textWithActions, usersMap)

  const textWithMergeFields = textWithMentions.replace(/`?(\{[^}]+\})`?/g, (_, template) => {
    const mergeField = allMergeFields.find((field) => field.template === template)
    if (!mergeField) return parseFallbackTemplate(template)

    const integrationLabel = INTEGRATION_DISPLAY_NAME[mergeField.integrationKey]
    return integrationLabel ? `${mergeField.label} (${integrationLabel})` : mergeField.label
  })

  const textWithFlags = textWithMergeFields.replace(
    settingsFlagPattern,
    (_, flag: string) => SETTINGS_FLAG_LABELS[flag] ?? flag
  )

  return textWithFlags.replace(
    widgetIdPattern,
    (_, id: keyof typeof WIDGET_ID_LABELS) => `"${WIDGET_ID_LABELS[id]}"`
  )
}

export const renderMessageContent = (
  content: string,
  allMergeFields: MergeField[],
  usersMap: Map<number, IUser>
): string =>
  content
    .split('\n')
    .map((line) => {
      if (line.startsWith('A: ')) {
        return `A: ${parseActionAnswer(line.slice(3), usersMap)}`
      }
      return replaceInlineActions(line, allMergeFields, usersMap)
    })
    .join('\n')
